import { faArchive, faChevronDown } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import type { ReactElement } from 'react'
import { useCallback, useMemo } from 'react'
import DataTable, {
  DataTableFilterableCell,
  type DataTableQueryParams,
  type DataTableRowSelection,
  defineColumns
} from '~/components/DataTable/DataTable'
import {
  cellContentMain,
  cellContentRowMain,
  cellContentSub
} from '~/components/DataTable/DataTable.css'
import type { FiltersState } from '~/components/Filters/FilterBar/types'
import { Badge } from '~/components/Global/Blocks/Badge/Badge'
import { NameCircle } from '~/components/Global/Elements/NameCircle/NameCircle'
import { ProfilePicture } from '~/components/Global/Elements/ProfilePicture/ProfilePicture'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { CompanyContactsSubTable } from '~/domains/companies/components/CompanyContactsSubTable'
import type { CompanyListItem } from '~/domains/companies/types'
import { useConfig } from '~/domains/configuration/queries/config'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import type { TaxonomyHead } from '~/domains/taxonomies/types'
import { UsersList } from '~/domains/users/components/UsersList'
import { formatDateTime } from '~/utils/date'
import { formatName } from '~/utils/names'
import {
  archivedIcon,
  companyCell,
  companyCellContent,
  contactsToggleChevron,
  contactsToggleChevronExpanded,
  noContact
} from './CompaniesTable.css'

type CompaniesTableProps = {
  data: CompanyListItem[]
  totalItems: number
  queryParams: DataTableQueryParams
  filters: FiltersState
  rowSelection: DataTableRowSelection
  onRowSelectionChange: (selection: DataTableRowSelection) => void
  selectedContactIds: number[]
  onToggleContact: (contactId: number, companyId: number) => void
  onQueryChange: (params: DataTableQueryParams) => void
  onResetFilters?: () => void
  showResetFilters?: boolean
  isLoading?: boolean
  isFetching?: boolean
  onRefresh?: () => void
}

const toListOptions = (items: TaxonomyHead[] | undefined) =>
  items?.map((item) => ({ label: item.name, value: String(item.id) })) ?? []

const contactsLabel = (company: CompanyListItem): string => {
  if (company.matchingContactsCount === 1 && company.singleContact) {
    return formatName(company.singleContact) || '1 contact'
  }
  return `${company.matchingContactsCount} contacts`
}

export const CompaniesTable = ({
  data,
  totalItems,
  queryParams,
  filters,
  rowSelection,
  onRowSelectionChange,
  selectedContactIds,
  onToggleContact,
  onQueryChange,
  onResetFilters,
  showResetFilters,
  isLoading,
  isFetching,
  onRefresh
}: CompaniesTableProps): ReactElement => {
  const { data: legacyUrl = '' } = useConfig('organization.legacy_url')
  const { data: companyStatuses } = useTaxonomy('company_statuses')
  const { data: potentials } = useTaxonomy('potentials')
  const { data: sectors } = useTaxonomy('sectors')

  const renderExpandedRow = useCallback(
    (row: CompanyListItem) => (
      <CompanyContactsSubTable
        companyId={row.id}
        companyUrl={`${legacyUrl}/admin/entreprises/edit/${row.id}`}
        filters={filters}
        selectedContactIds={selectedContactIds}
        onToggleContact={onToggleContact}
      />
    ),
    [legacyUrl, filters, selectedContactIds, onToggleContact]
  )

  const columns = useMemo(
    () =>
      defineColumns<CompanyListItem>([
        {
          id: 'name',
          header: 'Raison sociale',
          defaultSize: 320,
          accessor: 'name',
          enableSorting: true,
          enableColumnFilter: true,
          meta: { sticky: true, filterPlaceholder: 'Rechercher...' },
          cell: ({ row, isExpanded, toggleExpanded }) => (
            <div className={companyCell}>
              {row.logo ? (
                <ProfilePicture src={row.logo} />
              ) : (
                <NameCircle name={row.name} />
              )}
              <div className={companyCellContent}>
                <a
                  href={`${legacyUrl}/admin/entreprises/edit/${row.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className={clsx(cellContentRowMain, cellContentMain)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {row.name}
                </a>
                {row.archived ? (
                  <Tooltip text="Entreprise archivée" direction="top" asChild>
                    <FontAwesomeIcon
                      icon={faArchive}
                      className={archivedIcon}
                    />
                  </Tooltip>
                ) : null}
                <br />
                {row.matchingContactsCount > 0 ? (
                  <Button
                    type="button"
                    variation={ButtonVariation.Link}
                    aria-expanded={isExpanded}
                    buttonSize="extraSmall"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleExpanded()
                    }}
                  >
                    {contactsLabel(row)}
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={clsx(
                        contactsToggleChevron,
                        isExpanded && contactsToggleChevronExpanded
                      )}
                    />
                  </Button>
                ) : (
                  <span className={clsx(cellContentSub, noContact)}>
                    Aucun contact correspondant
                  </span>
                )}
              </div>
            </div>
          )
        },
        {
          id: 'companyStatus',
          header: 'Statut',
          defaultSize: 140,
          accessor: (row) => row.companyStatus?.name ?? null,
          enableSorting: false,
          enableColumnFilter: true,
          meta: {
            filterType: 'list',
            filterListOptions: toListOptions(companyStatuses),
            filterListEmptyLabel: 'Tous'
          },
          cell: ({ row, filterByValue }) =>
            row.companyStatus ? (
              <DataTableFilterableCell
                filterValue={String(row.companyStatus.id)}
                onFilter={filterByValue}
              >
                <Badge
                  title={row.companyStatus.name}
                  backgroundColor={row.companyStatus.color}
                />
              </DataTableFilterableCell>
            ) : (
              '—'
            )
        },
        {
          id: 'potential',
          header: 'Potentiel',
          defaultSize: 120,
          accessor: (row) => row.potential?.name ?? null,
          enableSorting: false,
          enableColumnFilter: true,
          meta: {
            filterType: 'list',
            filterListOptions: toListOptions(potentials),
            filterListEmptyLabel: 'Tous'
          },
          cell: ({ row, filterByValue }) =>
            row.potential ? (
              <DataTableFilterableCell
                filterValue={String(row.potential.id)}
                onFilter={filterByValue}
              >
                {row.potential.name}
              </DataTableFilterableCell>
            ) : (
              '—'
            )
        },
        {
          id: 'lastActionAt',
          header: 'Dernière action',
          defaultSize: 150,
          accessor: 'lastActionAt',
          enableSorting: true,
          enableColumnFilter: true,
          meta: { filterType: 'date', cellMono: true },
          cell: ({ value }) =>
            value
              ? formatDateTime(new Date(String(value)), { dateStyle: 'short' })
              : 'Aucune'
        },
        {
          id: 'sector',
          header: 'Secteur',
          defaultSize: 200,
          accessor: (row) => row.sector?.name ?? null,
          enableSorting: false,
          enableColumnFilter: true,
          meta: {
            filterType: 'list',
            filterListOptions: toListOptions(sectors),
            filterListEmptyLabel: 'Tous',
            filterListSearchable: true
          },
          cell: ({ row, filterByValue }) =>
            row.sector ? (
              <DataTableFilterableCell
                filterValue={String(row.sector.id)}
                onFilter={filterByValue}
              >
                {row.sector.name}
              </DataTableFilterableCell>
            ) : (
              '—'
            )
        },
        {
          id: 'city',
          header: 'Ville',
          defaultSize: 200,
          accessor: (row) => row.city ?? null,
          enableSorting: false,
          enableColumnFilter: true,
          meta: { filterPlaceholder: 'Rechercher...' },
          cell: ({ row, value, filterByValue }) => {
            if (!value) return '—'
            return (
              <DataTableFilterableCell
                filterValue={String(value)}
                onFilter={filterByValue}
              >
                {[row.postcode, value].filter(Boolean).join(' ')}
              </DataTableFilterableCell>
            )
          }
        },
        {
          id: 'users',
          header: 'Utilisateurs',
          defaultSize: 160,
          cell: ({ row }) =>
            row.users.length > 0 ? (
              <UsersList users={row.users} align="left" />
            ) : (
              '—'
            )
        }
      ]),
    [legacyUrl, companyStatuses, potentials, sectors]
  )

  return (
    <DataTable
      columns={columns}
      data={data}
      totalItems={totalItems}
      queryParams={queryParams}
      defaultSorting={[{ id: 'name', desc: false }]}
      onQueryChange={onQueryChange}
      onResetFilters={onResetFilters}
      showResetFilters={showResetFilters}
      rowSelection={rowSelection}
      onRowSelectionChange={onRowSelectionChange}
      getRowId={(row) => String(row.id)}
      renderExpandedRow={renderExpandedRow}
      isLoading={isLoading}
      isFetching={isFetching}
      onRefresh={onRefresh}
      configName="tables.companies"
    />
  )
}
